import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSiteStore } from "../stores/siteStore";
import styles from "./Breadcrumb.module.css";

/** Routes naming ONE warehouse in the path — meaningless under a different site. */
const WAREHOUSE_SCOPED = /^\/admin\/warehouses\/[^/]+/;
/** A count report: its run belongs to one site and one warehouse (ADR 424). */
const RUN_SCOPED = /^\/inventory\/runs\/[^/]+/;

/**
 * The topbar context slot: active site / warehouse. One choice renders as
 * static text; several render as the picker (cache cleared on switch, as the
 * old topbar pickers did).
 */
export function Breadcrumb() {
  const { t } = useTranslation();
  const {
    availableSites, activeSiteId, setActiveSiteId,
    availableWarehouses, activeWarehouseId, setActiveWarehouseId,
  } = useSiteStore();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const activeSiteName = availableSites.find((s) => s.id === activeSiteId)?.name ?? activeSiteId;

  // ADR 229: a site switch invalidates any route scoped to one site's warehouse.
  // Leaving the id in the URL pairs the new site with the old warehouse, and every
  // call on that page 400s — so step back to the collection the switch still means.
  function switchSite(id: string) {
    void setActiveSiteId(id);
    qc.clear();
    if (WAREHOUSE_SCOPED.test(pathname)) navigate("/admin/warehouses");
    else if (RUN_SCOPED.test(pathname)) navigate("/inventory");
  }

  function switchWarehouse(id: string) {
    setActiveWarehouseId(id);
    qc.clear();
    if (RUN_SCOPED.test(pathname)) navigate("/inventory");
  }

  return (
    <div className={styles.crumb}>
      {availableSites.length > 1 ? (
        <select
          className={styles.picker}
          value={activeSiteId}
          aria-label={t("a11y.activeSite")}
          onChange={(e) => switchSite(e.target.value)}
        >
          {availableSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      ) : (
        <span className={styles.name}>{activeSiteName}</span>
      )}
      {availableWarehouses.length > 0 && <span className={styles.sep} aria-hidden="true">/</span>}
      {availableWarehouses.length > 1 ? (
        <select
          className={styles.picker}
          value={activeWarehouseId ?? ""}
          aria-label={t("a11y.activeWarehouse")}
          onChange={(e) => switchWarehouse(e.target.value)}
        >
          {availableWarehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      ) : availableWarehouses.length === 1 ? (
        <span className={styles.name}>{availableWarehouses[0].name}</span>
      ) : null}
    </div>
  );
}
